import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiError, fetchAdminShowcase, saveAdminShowcase, type ShowcaseLinks, acceptVideoRequest, declineVideoRequest, fetchAdminVideoRequests, type VideoRequestView, cancelAdminVideo, createAdminVideo, regenerateAdminVideoCuts, selectAdminVideoTakes, deleteAdminVideo, fetchAdminThemes, fetchAdminVideos, fetchTheme, fetchVideoChemistry, retryAdminVideo,
} from '../api/client';
import type { AdminTheme, AdminVideo, AdminVideos, CastRef, NewVideo, ThemeCharacter, VideoChemistry, VideoStatus } from '../api/types';
import { Portrait } from '../components/Portrait';
import { DEFAULT_VIDEO_PROMPT } from '../components/videoPrompt';

/**
 * 管理メニューの「動画」。診断のキャラを 2 人選び、2 人の相性で短いドラマ動画を作る（services/api/src/lib/video.ts）。
 * Gemini が絵コンテを書き、Gemini Omni が最初のカットを描いて、前のカットから延長を繰り返して繋ぐ。
 * 1 本で数分〜十数分かかるので、作ったら一覧をポーリングして進み具合を出す。
 */
const fail = (cause: unknown, fallback: string) => (cause instanceof ApiError ? cause.message : fallback);

const STATUS_LABEL: Record<VideoStatus, string> = {
  queued: '順番待ち',
  planning: '絵コンテを書いています',
  generating: 'カットを描いています',
  stitching: '繋いでいます',
  done: 'できました',
  failed: '失敗しました',
};

const EXAMPLES = [
  DEFAULT_VIDEO_PROMPT,
  '週末の旅行の行き先を決める。片方は比較表を作って迷い続け、もう片方は直感で即決して荷造りを始める',
  'はじめてのデートの店選び。予約と段取りを練る側と、その場のノリで歩き回る側',
  '引っ越しの準備。計画表を貼り出す側と、思いつきで模様替えを始める側。最後は二人で完璧な部屋に',
];

const working = (status: VideoStatus) => status !== 'done' && status !== 'failed';
const assetUrl = (path: string | null) => (path ? (path.startsWith('/') ? path : `/${path}`) : null);
const yen = (usd: number) => `約 ${Math.round(usd * 150).toLocaleString('ja-JP')} 円`;

export function VideosTab({ onError }: { readonly onError: (message: string) => void }) {
  const [data, setData] = useState<AdminVideos | null>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await fetchAdminVideos());
    } catch (cause) {
      onError(fail(cause, '動画の一覧を読み込めませんでした。'));
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  // 作っている途中のものがあれば 5 秒ごとに読み直す
  const anyWorking = data?.videos.some((v) => working(v.status)) ?? false;
  useEffect(() => {
    if (!anyWorking) return undefined;
    const timer = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(timer);
  }, [anyWorking, load]);

  const act = async (videoId: string, run: () => Promise<unknown>) => {
    setBusy(videoId);
    try {
      await run();
      await load();
    } catch (cause) {
      onError(fail(cause, 'できませんでした。'));
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="admin__panel">
      <VideoRequests onError={onError} onAccepted={() => void load()} yenPerVideo={data ? yen(32 * data.pricePerSecond) : null} />
      {data && <ShowcaseSettings videos={data.videos} onError={onError} />}
      <h2 className="section__title">相性ドラマの動画を作る</h2>
      <p className="page__lead">
        診断のキャラを 2 人選ぶと、2 人の型の違い（どこでぶつかり、どこで補い合うか）から短いドラマ動画を作ります。
        喋らせる動画（既定）は、カットごとに 2 人の元の絵から新しく描いて繋ぎます（繋ぎ方で「前のカットの最後から続ける」を選ぶと、前のカットの最後のコマも渡します）。喋らせない動画は、最初のカットのあと前のカットを延長して繋ぎます。1 本 5〜15 分ほどかかります。
      </p>
      <div className="video-warning" role="note">
        <strong>注意: 長くするほど崩れます。</strong>
        1 カットは {data ? `${Math.min(...data.limits.sceneSecChoices)}〜${Math.max(...data.limits.sceneSecChoices)}` : '4〜10'} 秒で選べます（既定 {data?.limits.sceneSec ?? 8} 秒）。喋らせない動画は Gemini Omni の延長で繋ぎ、延長は {data?.limits.extendMaxSec ?? 40} 秒まで続けられますが、
        延長を重ねるほど <strong>絵・キャラの見た目が大きく崩れる</strong>ことがあります（服や持ち物が消える、別人になる）。
        延長では台詞を喋らせられないため、喋らせる動画は 1 カットずつ新しく描いて繋ぎ、{data ? (data.limits.maxSec % 60 === 0 ? `${data.limits.maxSec / 60} 分` : `${data.limits.maxSec} 秒`) : '2 分'}まで作れます。
        ただし長いほど <strong>声・見た目・日本語の台詞のばらつき</strong>が増えます（声が少し変わる、台詞がどもる・おかしな日本語になる）。
        声は毎カット同じ声の指定で頼み、1 カット目と聞き比べて別人に聞こえたカットは 1 回だけ描き直します。まずは {data?.limits.minSec ?? 30} 秒で作り、できあがりを見てから長くしてください。
      </div>
      {data && (
        <p className="admin__sub">
          今日 {data.quota.used} 本{data.quota.limit === null ? '（上限なし）' : ` ／ 上限 ${data.quota.limit} 本`}　モデル {data.model}
        </p>
      )}
      <VideoForm
        limits={data?.limits ?? null}
        pricePerSecond={data?.pricePerSecond ?? 0.1}
        remaining={data?.quota.remaining ?? null}
        onError={onError}
        onCreated={() => void load()}
      />

      <h2 className="section__title">作った動画</h2>
      {data === null ? <p className="page__notice">読み込んでいます…</p> : data.videos.length === 0 ? (
        <p className="page__notice">まだありません。</p>
      ) : (
        <ul className="admin__themes">
          {data.videos.map((video) => (
            <VideoCard key={video.videoId} video={video} busy={busy === video.videoId}
              onRetry={(fresh) => void act(video.videoId, () => retryAdminVideo(video.videoId, fresh))}
              onRegenerate={(cuts) => {
                const total = cuts.reduce((sum, c) => sum + c.count, 0);
                const usd = total * (video.sceneSec ?? 10) * (data?.pricePerSecond ?? 0.1);
                if (!window.confirm(`${cuts.map((c) => `カット ${c.index + 1} を ${c.count} 本`).join('、')}作り直します（計 ${total} 本・見積もり ${yen(usd)}）。できあがりは変わりません。見比べてから「選んだ動画で繋ぎ直す」を押してください。`)) return;
                void act(video.videoId, () => regenerateAdminVideoCuts(video.videoId, cuts));
              }}
              onRestitch={(selection) => void act(video.videoId, () => selectAdminVideoTakes(video.videoId, selection))}
              onCancel={() => {
                if (!window.confirm('作っている途中で止めます。できたカットは残り、あとで「続きから」で再開できます。描いている途中のカットは捨てます。')) return;
                void act(video.videoId, () => cancelAdminVideo(video.videoId));
              }}
              onDelete={() => {
                if (!window.confirm('この動画を消します。カットもできあがりも消え、元に戻せません。')) return;
                void act(video.videoId, () => deleteAdminVideo(video.videoId));
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** 選んだ 1 人（どの診断のどのキャラか）。 */
interface PickedChar {
  readonly themeId: string;
  readonly themeTitle: string;
  readonly id: string;
  readonly name: string;
}

const castOf = (p: PickedChar): CastRef => ({ themeId: p.themeId, characterId: p.id });

function VideoForm({ limits, pricePerSecond, remaining, onError, onCreated }: {
  readonly limits: AdminVideos['limits'] | null;
  readonly pricePerSecond: number;
  readonly remaining: number | null;
  readonly onError: (message: string) => void;
  readonly onCreated: () => void;
}) {
  const [themes, setThemes] = useState<readonly AdminTheme[]>([]);
  const [themeId, setThemeId] = useState('');
  const [characters, setCharacters] = useState<readonly ThemeCharacter[]>([]);
  // 選んだ 2 人は、診断を切り替えても残る（違う診断のキャラどうしでも作れる）
  const [picked, setPicked] = useState<readonly PickedChar[]>([]);
  const [chemistry, setChemistry] = useState<VideoChemistry | null>(null);
  const [prompt, setPrompt] = useState(DEFAULT_VIDEO_PROMPT);
  // 既定は 8 秒 × 4 カット（起承転結）= 32 秒
  const [sceneSec, setSceneSec] = useState(8);
  const [scenes, setScenes] = useState(4);
  useEffect(() => {
    if (!limits) return;
    setSceneSec((v) => (limits.sceneSecChoices.includes(v) ? v : limits.sceneSec));
  }, [limits]);
  const [aspectRatio, setAspectRatio] = useState<NewVideo['aspectRatio']>('9:16');
  const [resolution, setResolution] = useState<NewVideo['resolution']>('720p');
  const [captions, setCaptions] = useState(true);
  const [voice, setVoice] = useState(true);
  const [continuity, setContinuity] = useState<NewVideo['continuity']>('scene');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    void fetchAdminThemes('')
      .then(({ themes: list }) => {
        const alive = list.filter((t) => !t.deletedAt);
        setThemes(alive);
        setThemeId((current) => current || alive.find((t) => t.themeId === 'sengoku-gifu')?.themeId || alive[0]?.themeId || '');
      })
      .catch((cause: unknown) => onError(fail(cause, '診断の一覧を読み込めませんでした。')));
  }, [onError]);

  useEffect(() => {
    if (!themeId) { setCharacters([]); return; }
    void fetchTheme(themeId)
      .then((theme) => setCharacters(theme.characters))
      .catch((cause: unknown) => onError(fail(cause, 'キャラクターを読み込めませんでした。')));
  }, [themeId, onError]);

  useEffect(() => {
    setChemistry(null);
    if (picked.length !== 2) return;
    void fetchVideoChemistry(castOf(picked[0]), castOf(picked[1])).then(setChemistry).catch(() => setChemistry(null));
  }, [picked]);

  const themeTitle = useMemo(() => themes.find((t) => t.themeId === themeId)?.title ?? '', [themes, themeId]);
  const keyOf = (p: Pick<PickedChar, 'themeId'> & { id: string }) => `${p.themeId}/${p.id}`;
  const toggle = (c: ThemeCharacter) => {
    const next: PickedChar = { themeId, themeTitle, id: c.id, name: c.name };
    setPicked((current) => (current.some((p) => keyOf(p) === keyOf(next))
      ? current.filter((p) => keyOf(p) !== keyOf(next))
      : current.length >= 2 ? [current[1], next] : [...current, next]));
  };
  const cross = picked.length === 2 && picked[0].themeId !== picked[1].themeId;

  // 目標の長さに近いカット数（サーバの sceneCountFor と同じ）
  const durationSec = scenes * sceneSec;
  // 喋らせない動画は延長で繋ぐので 40 秒まで、喋らせる動画は 2 分まで
  const maxSec = voice ? (limits?.maxSec ?? 120) : (limits?.extendMaxSec ?? 40);
  const minSec = limits?.minSec ?? 30;
  const lengthOk = durationSec >= minSec && durationSec <= maxSec;
  const estimate = durationSec * pricePerSecond;
  const sceneChoices = Array.from({ length: (limits?.maxScenes ?? 12) - (limits?.minScenes ?? 3) + 1 }, (_, i) => (limits?.minScenes ?? 3) + i);
  const ready = picked.length === 2 && !sending && remaining !== 0 && lengthOk;

  const submit = async () => {
    if (!ready) return;
    const [a, b] = picked.map((p) => p.name);
    if (!window.confirm(`${a} × ${b} の動画（${scenes} カット × ${sceneSec} 秒 = ${durationSec} 秒）を作ります。見積もり ${yen(estimate)}（$${estimate.toFixed(2)}）。サイト全体の動画の上限を 1 本使います。`)) return;
    setSending(true);
    try {
      await createAdminVideo({ cast: [castOf(picked[0]), castOf(picked[1])], prompt: prompt.trim(), sceneCount: scenes, sceneSec, aspectRatio, resolution, captions, voice, continuity });
      setPrompt(DEFAULT_VIDEO_PROMPT);
      onCreated();
    } catch (cause) {
      onError(fail(cause, '動画を作れませんでした。'));
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="video-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <label className="field">
        <span className="field__label">診断</span>
        <select className="field__input" value={themeId} disabled={sending} onChange={(e) => setThemeId(e.target.value)}>
          {themes.map((t) => <option key={t.themeId} value={t.themeId}>{t.title}（{t.characterCount} 人）</option>)}
        </select>
      </label>

      <fieldset className="video-form__cast">
        <legend className="field__label">キャラクターを 2 人選ぶ（{picked.length}/2）</legend>
        <p className="admin__sub">診断を切り替えても選んだキャラは残るので、違う診断のキャラどうしでも作れます。</p>
        {picked.length > 0 && (
          <ol className="video-form__picked">
            {picked.map((p) => (
              <li key={keyOf(p)}>
                {p.name}<span className="admin__sub">（{p.themeTitle}）</span>
                <button type="button" className="button button--ghost button--small" disabled={sending} onClick={() => setPicked((current) => current.filter((x) => keyOf(x) !== keyOf(p)))}>外す</button>
              </li>
            ))}
          </ol>
        )}
        <ul className="video-form__chars">
          {characters.map((c) => {
            const order = picked.findIndex((p) => keyOf(p) === keyOf({ themeId, id: c.id }));
            return (
              <li key={c.id}>
                <button type="button" className={`video-form__char${order >= 0 ? ' video-form__char--on' : ''}`} aria-pressed={order >= 0} disabled={sending} onClick={() => toggle(c)}>
                  {order >= 0 && <span className="video-form__order">{order + 1}</span>}
                  <Portrait brief={{ name: c.name, portrait: c.portrait, emoji: c.emoji }} size={56} className="video-form__portrait" loading="lazy" />
                  <span className="video-form__name">{c.name}</span>
                  <span className="video-form__type">{c.type.split('-')[0]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {picked.length === 2 && (
        <div className="video-form__chemistry" aria-live="polite">
          <strong>{picked[0].name} × {picked[1].name}</strong>
          {cross && <p className="admin__sub">別々の診断の 2 人です。出会う場面から話を考えます。</p>}
          {chemistry ? (
            <ul>
              {chemistry.different.map((d) => <li key={d}>{d}</li>)}
              {chemistry.same.map((d) => <li key={d} className="admin__sub">{d}</li>)}
            </ul>
          ) : <p className="admin__sub">軸の違いを読んでいます…</p>}
        </div>
      )}

      <label className="field">
        <span className="field__label">どんな場面にするか（既定はおまかせ。場面を決めたいときは書き換える）</span>
        <textarea className="field__input" rows={3} maxLength={500} value={prompt} disabled={sending}
          placeholder={EXAMPLES[1]} onChange={(e) => setPrompt(e.target.value)} />
      </label>
      <div className="video-form__examples">
        {EXAMPLES.map((example) => (
          <button key={example} type="button" className="chip" disabled={sending} onClick={() => setPrompt(example)}>{example === DEFAULT_VIDEO_PROMPT ? 'おまかせ' : example.split('。')[0]}</button>
        ))}
      </div>

      <div className="admin__limits">
        <label className="field">
          <span className="field__label">カット数</span>
          <select className="field__input" value={scenes} disabled={sending} onChange={(e) => setScenes(Number(e.target.value))}>
            {sceneChoices.map((n) => <option key={n} value={n}>{n} カット{n === 4 ? '（起承転結）' : ''}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">1 カットの長さ</span>
          <select className="field__input" value={sceneSec} disabled={sending} onChange={(e) => setSceneSec(Number(e.target.value))}>
            {(limits?.sceneSecChoices ?? [4, 6, 8, 10]).map((n) => <option key={n} value={n}>{n} 秒</option>)}
          </select>
        </label>
        <div className="field">
          <span className="field__label">長さ</span>
          <span className={`video-form__total${lengthOk ? '' : ' video-form__total--ng'}`}>
            {durationSec >= 60 ? `${Math.floor(durationSec / 60)} 分${durationSec % 60 ? ` ${durationSec % 60} 秒` : ''}` : `${durationSec} 秒`}
            {!lengthOk && `（${minSec}〜${maxSec} 秒にしてください）`}
          </span>
        </div>
        <label className="field">
          <span className="field__label">向き</span>
          <select className="field__input" value={aspectRatio} disabled={sending} onChange={(e) => setAspectRatio(e.target.value as NewVideo['aspectRatio'])}>
            <option value="9:16">縦 9:16（ショート・リール）</option>
            <option value="16:9">横 16:9</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">画質</span>
          <select className="field__input" value={resolution} disabled={sending} onChange={(e) => setResolution(e.target.value as NewVideo['resolution'])}>
            <option value="720p">720p</option>
            <option value="1080p">1080p（拡大）</option>
            <option value="360p">360p（軽い）</option>
          </select>
        </label>
      </div>
      <label className="video-form__check">
        <input type="checkbox" checked={voice} disabled={sending} onChange={(e) => setVoice(e.target.checked)} />
        2 人に台詞を喋らせる（掛け合い。日本語の声）
      </label>
      {voice && (
        <label className="field">
          <span className="field__label">カットの繋ぎ方</span>
          <select className="field__input" value={continuity} disabled={sending} onChange={(e) => setContinuity(e.target.value as NewVideo['continuity'])}>
            <option value="scene">カットごとに場面を切り替える（おすすめ。キャラが崩れにくい）</option>
            <option value="frame">前のカットの最後から続ける（絵は繋がるが、重ねるほど崩れやすい）</option>
          </select>
        </label>
      )}
      <label className="video-form__check">
        <input type="checkbox" checked={captions} disabled={sending} onChange={(e) => setCaptions(e.target.checked)} />
        台詞の字幕と、2 人の型と名前の見出しを焼き込む
      </label>

      <div className="page__actions">
        <button className="button button--primary" type="submit" disabled={!ready}>
          {sending ? '頼んでいます…' : '動画を作る'}
        </button>
        <span className="admin__sub">見積もり {yen(estimate)}（${estimate.toFixed(2)}。請求額ではありません）</span>
      </div>
      {remaining === 0 && <p className="page__notice">今日の動画の上限に達しています。「上限」のタブで変えられます。</p>}
    </form>
  );
}

function VideoCard({ video, busy, onRetry, onRegenerate, onRestitch, onCancel, onDelete }: {
  readonly video: AdminVideo;
  readonly busy: boolean;
  readonly onRetry: (fresh: boolean) => void;
  readonly onRegenerate: (cuts: readonly { readonly index: number; readonly count: number }[]) => void;
  readonly onRestitch: (selection: readonly number[]) => void;
  readonly onCancel: () => void;
  readonly onDelete: () => void;
}) {
  const [a, b] = video.characters;
  const progress = video.total > 0 ? Math.round((video.done / video.total) * 100) : 0;
  const url = assetUrl(video.url);
  return (
    <li className="admin__theme video-card">
      <div className="video-card__head">
        <div className="video-card__faces">
          {[a, b].map((c) => c && (
            <Portrait key={c.id} brief={{ name: c.name, portrait: assetUrl(c.portrait), emoji: c.emoji }} size={40} className="video-form__portrait" />
          ))}
        </div>
        <div className="admin__theme-head">
          <span className="admin__theme-title">{video.storyboard?.title || `${a?.name} × ${b?.name}`}</span>
          <span className="admin__sub">
            {video.themeTitle} ／ {a?.name}（{a?.type.split('-')[0]}）× {b?.name}（{b?.type.split('-')[0]}）／ {new Date(video.createdAt).toLocaleString('ja-JP')}
          </span>
          <span className="admin__sub admin__prompt">{video.prompt}</span>
        </div>
      </div>

      <dl className="admin__theme-meta">
        <div><dt>状態</dt><dd>{video.regen && video.regen.length > 0 && working(video.status)
          ? `カット ${[...new Set(video.regen)].map((n) => n + 1).join('・')} を作り直しています（残り ${video.regen.length} 本）`
          : `${STATUS_LABEL[video.status]}${video.status === 'generating' ? `（${video.done}/${video.total}）` : ''}`}</dd></div>
        <div><dt>長さ</dt><dd>{video.outputSec ? `${video.outputSec} 秒` : `目標 ${video.durationSec} 秒`}（{video.total} カット × {video.sceneSec ?? 10} 秒）</dd></div>
        <div><dt>形</dt><dd>{video.aspectRatio} ／ {video.resolution}{video.voice ? ' ／ 台詞あり' : ''}</dd></div>
        <div><dt>見積もり</dt><dd>{yen(video.estimateUsd)}</dd></div>
      </dl>

      {working(video.status) && (
        <div className="video-card__progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <span style={{ width: `${Math.max(4, progress)}%` }} />
        </div>
      )}
      {video.error && <p className="page__notice">{video.error}</p>}
      {video.storyboard?.chemistry && <p className="video-card__chemistry">{video.storyboard.chemistry}</p>}

      {url && (
        <div className={`video-card__player video-card__player--${video.aspectRatio === '9:16' ? 'tall' : 'wide'}`}>
          <video src={url} controls playsInline preload="metadata" />
        </div>
      )}

      {!working(video.status) && video.cuts && video.cuts.length === video.total && video.total > 0 && (
        <CutPicker video={video} busy={busy} onRegenerate={onRegenerate} onRestitch={onRestitch} />
      )}

      {video.storyboard && (
        <details className="video-card__board">
          <summary>絵コンテ（{video.storyboard.scenes.length} カット）</summary>
          <ol>
            {video.storyboard.scenes.map((scene, i) => (
              <li key={i}>
                {scene.lines && scene.lines.length > 0
                  ? scene.lines.map((line, j) => {
                    const name = video.storyboard?.cast.find((c) => c.id === line.speaker)?.callName
                      ?? video.characters.find((c) => c.id === line.speaker)?.name ?? '';
                    return <strong key={j}>{line.start.toFixed(1)}s {name}「{line.text}」{line.kind === 'reaction' ? '（合いの手）' : ''}</strong>;
                  })
                  : scene.caption && <strong>{scene.caption}</strong>}
                <span className="admin__sub">{scene.visual}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      <div className="admin__actions">
        {url && <a className="button button--ghost button--small" href={url} download={`gendan-${video.videoId}.mp4`}>ダウンロード</a>}
        {video.status === 'failed' && (
          <>
            <button className="button button--ghost button--small" type="button" disabled={busy} onClick={() => onRetry(false)}>
              続きから（{video.done}/{video.total} カットできています）
            </button>
            <button className="button button--ghost button--small" type="button" disabled={busy}
              onClick={() => { if (window.confirm('絵コンテから作り直します。できたカットは捨てます。')) onRetry(true); }}>絵コンテから作り直す</button>
          </>
        )}
        {working(video.status) && (
          <button className="button button--danger button--small" type="button" disabled={busy} onClick={onCancel}>キャンセル</button>
        )}
        {!working(video.status) && (
          <button className="button button--danger button--small" type="button" disabled={busy} onClick={onDelete}>消す</button>
        )}
      </div>
    </li>
  );
}

/**
 * カットの差し替え。カットごとに案を並べて見比べ、繋ぐ動画をラジオボタンで選んで繋ぎ直す。
 * 作り直したいカットは本数（1 回に 3 本まで）を選んで「作り直す」を押すと、そのカットに案が増える（できあがりは変わらない）。
 */
function CutPicker({ video, busy, onRegenerate, onRestitch }: {
  readonly video: AdminVideo;
  readonly busy: boolean;
  readonly onRegenerate: (cuts: readonly { readonly index: number; readonly count: number }[]) => void;
  readonly onRestitch: (selection: readonly number[]) => void;
}) {
  const cuts = video.cuts ?? [];
  const maxTakes = video.maxTakes ?? 10;
  const perRun = video.maxTakesPerRun ?? 3;
  const current = cuts.map((c) => c.selected);
  const [selection, setSelection] = useState<readonly number[]>(current);
  const [counts, setCounts] = useState<Readonly<Record<number, number>>>({});
  const currentKey = current.join(',');
  const takesKey = cuts.map((c) => c.takes.length).join(',');
  // 案が増えたら、選択は使っている案に合わせ直し、本数は 0 に戻す
  useEffect(() => { setSelection(currentKey.split(',').map(Number)); setCounts({}); }, [currentKey, takesKey]);
  const changed = selection.some((n, i) => n !== current[i]);
  const requests = Object.entries(counts).map(([index, count]) => ({ index: Number(index), count })).filter((r) => r.count > 0).sort((x, y) => x.index - y.index);
  const total = requests.reduce((sum, r) => sum + r.count, 0);
  const tall = video.aspectRatio === '9:16';
  const scenes = video.storyboard?.scenes ?? [];

  return (
    <details className="cut-picker" open={changed || total > 0 || undefined}>
      <summary>カットを差し替える（1 回に 1 カット {perRun} 本まで作り直せます）</summary>
      <ol className="cut-picker__cuts">
        {cuts.map((cut) => {
          const scene = scenes[cut.index];
          const room = Math.min(perRun, maxTakes - cut.takes.length);
          return (
            <li key={cut.index} className="cut-picker__cut">
              <div className="cut-picker__head">
                <strong>カット {cut.index + 1}</strong>
                <span className="admin__sub">{(scene?.lines ?? []).map((l) => l.text).join(' ／ ') || scene?.caption || ''}</span>
                <label className="cut-picker__count">
                  作り直す
                  <select className="field__input field__input--tiny" disabled={busy || room <= 0} value={counts[cut.index] ?? 0}
                    onChange={(e) => setCounts((prev) => ({ ...prev, [cut.index]: Number(e.target.value) }))}>
                    {Array.from({ length: Math.max(0, room) + 1 }, (_, n) => <option key={n} value={n}>{n} 本</option>)}
                  </select>
                  {room <= 0 && <span className="admin__sub">（{maxTakes} 本まで）</span>}
                </label>
              </div>
              <fieldset className="cut-picker__group">
                <legend className="admin__sub">繋ぐ動画（{cut.takes.length} 本から 1 つ選ぶ）</legend>
                <div className="cut-picker__takes">
                  {cut.takes.map((take, t) => (
                    <label key={take.url} className={`cut-picker__take${selection[cut.index] === t ? ' cut-picker__take--on' : ''}${tall ? ' cut-picker__take--tall' : ''}`}>
                      <video src={take.url} controls playsInline preload="metadata" />
                      <span className="cut-picker__take-foot">
                        <input type="radio" name={`take-${video.videoId}-${cut.index}`} disabled={busy} checked={selection[cut.index] === t}
                          onChange={() => setSelection((prev) => prev.map((n, i) => (i === cut.index ? t : n)))} />
                        {t + 1} 本目{t === current[cut.index] ? '（使用中）' : ''}
                        {take.linesOk === false && <span className="cut-picker__warn">台詞にずれ</span>}
                        {take.screenText && <span className="cut-picker__warn" title={take.screenText}>画面に文字</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </li>
          );
        })}
      </ol>
      <div className="admin__actions">
        <button className="button button--ghost button--small" type="button" disabled={busy || total === 0}
          onClick={() => onRegenerate(requests)}>
          作り直す（計 {total} 本）
        </button>
        <button className="button button--primary button--small" type="button" disabled={busy || !changed}
          onClick={() => onRestitch(selection)}>
          選んだ動画で繋ぎ直す
        </button>
      </div>
    </details>
  );
}

/**
 * 利用者からのリクエスト（doc/VIDEO.md §8）。待っているものを上に出し、「作る」（既定の 4 カット × 8 秒・720p・縦）か「見送る」を決める。
 * 作ると下の「作った動画」に並び、できたら本人の自分のページに届く。
 */
function VideoRequests({ onError, onAccepted, yenPerVideo }: {
  readonly onError: (message: string) => void;
  readonly onAccepted: () => void;
  readonly yenPerVideo: string | null;
}) {
  const [items, setItems] = useState<readonly VideoRequestView[] | null>(null);
  const [busy, setBusy] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [resolution, setResolution] = useState<NewVideo['resolution']>('720p');
  const load = useCallback(async () => {
    try { setItems((await fetchAdminVideoRequests()).requests); } catch (cause) { onError(fail(cause, 'リクエストを読み込めませんでした。')); }
  }, [onError]);
  useEffect(() => { void load(); }, [load]);

  const run = async (id: string, action: () => Promise<unknown>, after?: () => void) => {
    setBusy(id);
    try { await action(); await load(); after?.(); } catch (cause) { onError(fail(cause, '操作できませんでした。')); } finally { setBusy(''); }
  };
  const accept = (r: VideoRequestView) => {
    if (!window.confirm(`「${r.characterNames[0]} × ${r.characterNames[1]}」を作ります（4 カット × 8 秒・${resolution}${yenPerVideo ? `・${yenPerVideo}` : ''}。今日の上限を 1 本使います）。よろしいですか？`)) return;
    void run(r.requestId, () => acceptVideoRequest(r.requestId, { resolution }), onAccepted);
  };
  const decline = (r: VideoRequestView) => {
    const note = window.prompt('見送る理由（任意・本人に見えます）', '');
    if (note === null) return;
    void run(r.requestId, () => declineVideoRequest(r.requestId, note));
  };

  if (!items) return null;
  const pending = items.filter((r) => r.status === 'pending');
  const shown = showAll ? items : pending;
  return (
    <div className="admin__subpanel">
      <h2 className="section__title">利用者からのリクエスト（待っている {pending.length} 件）</h2>
      <p className="section__note">お金がかかるので、作るのはごく一部だけ。場面の注文は本人の文をそのまま絵コンテに渡します（不適切なものは見送る）。</p>
      <label className="field">
        <span className="field__label">「作る」ときの画質</span>
        <select className="field__input" value={resolution} onChange={(e) => setResolution(e.target.value as NewVideo['resolution'])}>
          <option value="360p">360p（軽い）</option>
          <option value="720p">720p</option>
          <option value="1080p">1080p</option>
        </select>
      </label>
      {shown.length === 0 && <p className="page__notice">{showAll ? 'リクエストはまだありません。' : '待っているリクエストはありません。'}</p>}
      <ul className="admin__list">
        {shown.map((r) => (
          <li key={r.requestId}>
            <strong>{r.characterNames[0]} × {r.characterNames[1]}</strong>
            <span className="page__footnote">（{r.themeTitle}・{new Date(r.createdAt).toLocaleString('ja-JP', { hour12: false })}・{r.status}{r.video ? `・動画 ${r.video.status}` : ''}）</span>
            <div>「{r.prompt}」</div>
            {r.note && <div className="page__footnote">見送った理由: {r.note}</div>}
            {r.status === 'pending' && (
              <div className="page__actions">
                <button className="button button--primary button--small" type="button" disabled={busy === r.requestId} onClick={() => accept(r)}>作る</button>
                <button className="button button--ghost button--small" type="button" disabled={busy === r.requestId} onClick={() => decline(r)}>見送る</button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <button className="button button--ghost button--small" type="button" onClick={() => setShowAll((v) => !v)}>{showAll ? '待っているものだけ' : '決まったものも見る'}</button>
    </div>
  );
}

/**
 * トップページの下に出す動画（3 本まで・選んだ順）と SNS のリンク（doc/VIDEO.md §9）。置いたものだけトップに出る。
 */
function ShowcaseSettings({ videos, onError }: { readonly videos: readonly AdminVideo[]; readonly onError: (message: string) => void }) {
  const [picked, setPicked] = useState<readonly string[] | null>(null);
  const [links, setLinks] = useState<ShowcaseLinks>({ x: null, instagram: null, youtube: null, tiktok: null });
  const [max, setMax] = useState(3);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  useEffect(() => {
    void fetchAdminShowcase()
      .then((r) => { setPicked(r.videoIds); setLinks(r.links); setMax(r.max); })
      .catch((cause: unknown) => onError(fail(cause, 'トップページの設定を読めませんでした。')));
  }, [onError]);

  if (!picked) return null;
  const done = videos.filter((v) => v.status === 'done' && v.url);
  const toggle = (id: string) => {
    setSaved('');
    setPicked((cur) => (cur!.includes(id) ? cur!.filter((x) => x !== id) : cur!.length >= max ? cur! : [...cur!, id]));
  };
  const save = async () => {
    setBusy(true); setSaved('');
    try {
      const r = await saveAdminShowcase({ videoIds: picked, links });
      setPicked(r.videoIds); setLinks(r.links); setSaved('保存しました。トップページには 1 分ほどで出ます。');
    } catch (cause) {
      onError(fail(cause, '保存できませんでした。'));
    } finally {
      setBusy(false);
    }
  };
  const field = (key: keyof ShowcaseLinks, label: string, placeholder: string) => (
    <label className="field" key={key}>
      <span className="field__label">{label}</span>
      <input className="field__input" type="url" value={links[key] ?? ''} placeholder={placeholder}
        onChange={(e) => { setSaved(''); setLinks((cur) => ({ ...cur, [key]: e.target.value || null })); }} />
    </label>
  );

  return (
    <div className="admin__subpanel">
      <h2 className="section__title">トップページに出す動画と SNS</h2>
      <p className="section__note">できあがった動画から {max} 本まで、選んだ順にトップページのいちばん下に並びます（パソコンは横に 3 本、スマホは横にスワイプ）。SNS のリンクは、置いたものだけ出ます。</p>
      {done.length === 0 && <p className="page__notice">できあがった動画がまだありません。</p>}
      <ul className="admin__list">
        {done.map((v) => {
          const order = picked.indexOf(v.videoId);
          return (
            <li key={v.videoId}>
              <label className="field field--check">
                <input type="checkbox" checked={order >= 0} disabled={order < 0 && picked.length >= max} onChange={() => toggle(v.videoId)} />
                <span>
                  {order >= 0 && <strong>{order + 1} 番目　</strong>}
                  {v.characters.map((c) => c.name).join(' × ')}
                  <span className="page__footnote">（{v.themeTitle}・{new Date(v.createdAt).toLocaleDateString('ja-JP')}）</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {field('x', 'X', 'https://x.com/…')}
      {field('instagram', 'Instagram', 'https://www.instagram.com/…')}
      {field('youtube', 'YouTube', 'https://www.youtube.com/@…')}
      {field('tiktok', 'TikTok', 'https://www.tiktok.com/@…')}
      <div className="page__actions">
        <button className="button button--primary" type="button" disabled={busy} onClick={() => void save()}>{busy ? '保存しています…' : '保存する'}</button>
        {saved && <span className="section__note" role="status">{saved}</span>}
      </div>
    </div>
  );
}
